import { getNetworkStatus } from '../hooks/useNetworkStatus';

export class FileUploadService {
    constructor() {
        this.activeUploads = new Map();
        this.uploadQueue = [];
        this.isProcessing = false;
    }

    /**
     * Upload multiple files with concurrency control
     * @param {Array} files - Array of file objects to upload
     * @param {number} concurrency - Number of simultaneous uploads (default: 3)
     * @param {Function} uploadFunction - Function to upload individual file
     * @param {Object} callbacks - Callback functions
     * @param {Function} callbacks.onProgress - Progress callback (current, total, percentage)
     * @param {Function} callbacks.onSuccess - Success callback (file, result)
     * @param {Function} callbacks.onError - Error callback (file, error)
     * @param {Function} callbacks.onComplete - Complete callback (results)
     * @param {any} uploadParams - Additional parameters for upload function
     * @param {boolean} wifiOnly - Only upload when connected to WiFi
     * @param {boolean} stopOnError - Stop all uploads on first error (default: true)
     */
    async uploadFiles({
        files,
        concurrency = 3,
        uploadFunction,
        callbacks = {},
        uploadParams = {},
        wifiOnly = false,
        stopOnError = true,
    }) {
        const {
            onProgress = () => {
            },
            onSuccess = () => {
            },
            onError = () => {
            },
            onComplete = () => {
            },
        } = callbacks;

        // Validate inputs
        if (!Array.isArray(files) || files.length === 0) {
            throw new Error('Files array is required and must not be empty');
        }

        if (typeof uploadFunction !== 'function') {
            throw new Error('Upload function is required');
        }

        const totalFiles = files.length;
        const results = {
            successful: [],
            failed: [],
            total: totalFiles,
        };

        let completedCount = 0;
        let activeCount = 0;
        let shouldStop = false;
        let errorReported = false;
        const maxConcurrency = Math.min(concurrency, totalFiles);

        // Create upload tasks
        const uploadTasks = files.map((file, index) => ({
            id: `upload_${index}_${Date.now()}`,
            file,
            index,
            status: 'pending',
        }));

        // Progress tracking
        const updateProgress = () => {
            const percentage = Math.round((completedCount / totalFiles) * 100);
            onProgress(completedCount, totalFiles, percentage);
        };

        // Process individual upload
        const processUpload = async (task) => {
            try {
                // Check if we should stop before starting
                if (shouldStop) {
                    task.status = 'cancelled';
                    return;
                }

                if (wifiOnly) {
                    const net = await getNetworkStatus();
                    if (!net.isWifi) {
                        throw new Error(Object.R('errors.wifiDisconnect'));
                    }
                }

                // Double check if we should stop after network check
                if (shouldStop) {
                    task.status = 'cancelled';
                    return;
                }

                task.status = 'uploading';
                activeCount++;

                // Call the upload function with the file and additional parameters
                const result = await uploadFunction(task.file, uploadParams);

                // Check if we should stop after upload (in case stop was triggered during upload)
                if (shouldStop) {
                    task.status = 'cancelled';
                    return;
                }

                task.status = 'completed';
                results.successful.push({
                    file: task.file,
                    result,
                    index: task.index,
                });

                onSuccess(task.file, result);
            } catch (error) {
                task.status = 'failed';
                results.failed.push({
                    file: task.file,
                    error,
                    index: task.index,
                });

                // Handle stopOnError logic
                if (stopOnError) {
                    shouldStop = true;
                    // Mark all pending tasks as cancelled
                    uploadTasks.forEach(t => {
                        if (t.status === 'pending') {
                            t.status = 'cancelled';
                        }
                    });

                    // Call onError only once for the first error
                    if (!errorReported) {
                        errorReported = true;
                        onError(task.file, error);
                    }
                } else {
                    // Normal error handling when stopOnError is false
                    onError(task.file, error);
                }
            } finally {
                activeCount--;
                completedCount++;
                updateProgress();
            }
        };

        return new Promise((resolve) => {
            const processNext = async () => {
                // Stop processing if shouldStop is true
                if (shouldStop) {
                    if (activeCount === 0) {
                        onComplete(results);
                        resolve(results);
                    }
                    return;
                }

                const nextTask = uploadTasks.find((task) => task.status === 'pending');

                if (!nextTask) {
                    if (activeCount === 0) {
                        onComplete(results);
                        resolve(results);
                    }
                    return;
                }

                if (activeCount < maxConcurrency && !shouldStop) {
                    processUpload(nextTask)
                        .then(() => {
                            processNext(); // Process next task after completion
                        });

                    if (activeCount < maxConcurrency - 1 && !shouldStop) {
                        setTimeout(processNext, 0);
                    }
                }
            };

            for (let i = 0; i < maxConcurrency; i++) {
                processNext();
            }

            updateProgress();
        });
    }

    cancelAllUploads() {
        this.activeUploads.clear();
        this.uploadQueue = [];
    }

    getUploadStats() {
        return {
            activeUploads: this.activeUploads.size,
            queuedUploads: this.uploadQueue.length,
        };
    }
}
